import React, { useMemo, useState } from 'react';
import { Checkbox, STYLE_TYPE } from 'baseui/checkbox';
import { NO_NAME_PROVIDED } from '../../../modelUtils/users';
import { Input } from 'baseui/input';
import { hasName } from '../../../utils';
import { humanizeEnumValue } from '../../../utils/humanizeEnumValue';
import { useStudentListLogic } from './hooks/useStudentListLogic';
import type { BeaconStudentRow } from './hooks/useStudentListLogic';
import {
  beaconSortField,
  beaconSurveyStatusCopy,
  beaconSurveyStatusOptions,
  beaconTriageOptions,
  NO_SUPPORTED_SURVEYS_MESSAGE,
  STUDENT_LIST_DISCLAIMER,
  toBeaconSurveyStatus,
  toBeaconTriageCategory,
  UNSUPPORTED_SURVEY_MESSAGE,
} from './constants';
import { StudentDetailModal } from './StudentDetailModal';
import { UnderlineButton } from '../../../Components/Buttons';
import { DataTable, TableSearch, TableToolbar } from '../../../Components/DataTable';
import type { DataTableColumn } from '../../../Components/DataTable';
import { Select } from '../../../Components/Form';
import { Icon } from '../../../Components/Icons';
import { LoadingPage } from '../../../Components/LoadingOverlay';
import { useFocusAfterRetry } from '../../../Hooks';
import { UnexpectedError } from '../../Shared';
import { colors, Text } from '../../../globalStyles';
import { ComplexNeedsPill } from '../../../Components/Beacon/ComplexNeedsPill';
import { ScoreBar } from '../../../Components/Beacon/ScoreBar';
import { TriagePill } from '../../../Components/Beacon/TriagePill';
import { BeaconSurveyStatus } from '../../../graphQL';

// Only a completed survey with a finished scoring run can open the detail modal. Partial and
// Pending rows render greyed out and inert — as do Complete rows whose scoring run has not
// finished yet (surveyResultId still null; see PDE-1380), which read "Complete" in the status
// column but stay inert until the run lands.
const getOpenableSurveyResultId = (row: BeaconStudentRow): number | null =>
  row.surveyStatus === BeaconSurveyStatus.Complete && row.surveyResultId != null
    ? row.surveyResultId
    : null;

export const StudentList = ({ organizationId }: { organizationId: number }) => {
  const vm = useStudentListLogic(organizationId);
  const [openSurveyResultId, setOpenSurveyResultId] = useState<number | null>(null);
  // "Try again" unmounts with the error state; focus lands on the survey dropdown once the list is back.
  const { focusTargetRef, retryWith } = useFocusAfterRetry<HTMLInputElement>(
    vm.status === 'success'
  );

  const columns = useMemo<DataTableColumn<BeaconStudentRow>[]>(
    () => [
      {
        key: beaconSortField.Name,
        title: 'Student',
        sortable: true,
        width: '3fr',
        render: row => (
          <div className="flex items-center">
            <div>
              <Text.bodyBold className={hasName(row) ? 'ttc' : undefined}>
                {hasName(row) ? row.reversedName ?? NO_NAME_PROVIDED : row.email}
              </Text.bodyBold>
              {hasName(row) && <Text.caption kind="grayText">{row.email}</Text.caption>}
            </div>
            {row.hasComplexNeeds && <ComplexNeedsPill className="ml3" />}
          </div>
        ),
      },
      {
        key: 'surveyStatus',
        title: 'Survey Status',
        render: row => (
          <Text.body>
            {beaconSurveyStatusCopy[row.surveyStatus] ?? humanizeEnumValue(row.surveyStatus)}
          </Text.body>
        ),
      },
      {
        key: 'triageCategory',
        title: 'Triage Category',
        align: 'center',
        render: row => <TriagePill category={row.triageCategory} />,
      },
      {
        key: beaconSortField.PersistenceIndex,
        title: 'Persistence Index',
        sortable: true,
        render: row => (
          <ScoreBar score={row.persistenceIndexScore} label="Persistence Index" showValue />
        ),
      },
      {
        key: beaconSortField.WellbeingIndex,
        title: 'Wellbeing Index',
        sortable: true,
        render: row => (
          <ScoreBar score={row.wellbeingIndexScore} label="Wellbeing Index" showValue />
        ),
      },
    ],
    []
  );

  if (vm.status === 'loading') {
    return <LoadingPage />;
  }

  if (vm.status === 'error') {
    return vm.onRetry ? (
      <Text.body role="alert">
        {vm.errorMessage}{' '}
        <UnderlineButton type="button" color={colors.link} onClick={retryWith(vm.onRetry)}>
          Try again
        </UnderlineButton>
      </Text.body>
    ) : (
      <UnexpectedError message={vm.errorMessage} />
    );
  }

  const { list } = vm;

  // Search, filters, and the Complex Needs toggle only make sense once a supported survey is chosen;
  // with no selection (or a V1 survey) the list is empty, so the controls stay hidden.
  const listControlsEnabled = vm.selectedSurveyId !== null && vm.isSelectedSupported;

  // Empty-list copy, org-level first: with no survey this list can render (no periods, or every one
  // predates student-search support) there is nothing to steer the provider to, so the no-surveys
  // message wins even when a listed V1 is picked. Otherwise a selected V1 shows the not-compatible
  // message, and no selection shows the prompt.
  const emptyState = !vm.hasSupportedSurveys ? (
    <Text.body>{NO_SUPPORTED_SURVEYS_MESSAGE}</Text.body>
  ) : !vm.isSelectedSupported ? (
    <Text.body>{UNSUPPORTED_SURVEY_MESSAGE}</Text.body>
  ) : vm.selectedSurveyId === null ? (
    <Text.body>Select a survey to view students.</Text.body>
  ) : undefined;

  return (
    <>
      <Text.bodySmall kind="grayText" className="mb3">
        {STUDENT_LIST_DISCLAIMER}
      </Text.bodySmall>
      {listControlsEnabled && (
        <TableSearch fullWidth>
          <Input
            aria-label="Search students"
            value={list.searchInput}
            onChange={event => list.setSearch(event.currentTarget.value)}
            startEnhancer={<Icon icon="iconsSearchSvg" alt="Search" />}
            placeholder="Search"
          />
        </TableSearch>
      )}
      <TableToolbar>
        <Select
          inputRef={focusTargetRef}
          options={vm.surveys}
          value={vm.selectedSurveyId}
          onChange={value =>
            list.setFilter('surveyId', typeof value === 'number' ? value : undefined)
          }
          placeholder="Select a survey"
        />
        {listControlsEnabled && (
          <>
            <Select
              options={beaconSurveyStatusOptions}
              value={list.filters.surveyStatus}
              onChange={value => list.setFilter('surveyStatus', toBeaconSurveyStatus(value))}
              placeholder="Survey Status"
              clearable
            />
            <Select
              options={beaconTriageOptions}
              value={list.filters.triageCategory}
              onChange={value => list.setFilter('triageCategory', toBeaconTriageCategory(value))}
              placeholder="Triage Category"
              clearable
            />
            <div>
              <Checkbox
                checked={list.filters.hasComplexNeeds === true}
                onChange={event =>
                  list.setFilter('hasComplexNeeds', event.currentTarget.checked ? true : undefined)
                }
                checkmarkType={STYLE_TYPE.toggle_round}
                labelPlacement="right"
              >
                <Text.bodyBold>Complex Needs</Text.bodyBold>
              </Checkbox>
            </div>
          </>
        )}
      </TableToolbar>
      <DataTable
        rows={list.rows}
        columns={columns}
        getRowKey={row => String(row.userId ?? row.email)}
        loading={list.loading}
        empty={emptyState}
        sort={list.sort}
        onToggleSort={list.toggleSort}
        pageInfo={list.pageInfo}
        onNextPage={list.nextPage}
        onPrevPage={list.prevPage}
        onRowClick={row => setOpenSurveyResultId(getOpenableSurveyResultId(row))}
        isRowDisabled={row => getOpenableSurveyResultId(row) === null}
      />
      {openSurveyResultId !== null && (
        <StudentDetailModal
          surveyResultId={openSurveyResultId}
          onClose={() => setOpenSurveyResultId(null)}
        />
      )}
    </>
  );
};
